import SearchIcon from '@mui/icons-material/Search';
import SwapVertical from '@mui/icons-material/SwapVert';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import type { MouseEvent, MouseEventHandler, ReactElement } from 'react';
import { Fragment, useContext, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { FilterWithFilterStates } from '~/shared/data-contract/filter-state';

import { ExportIconButton } from '../../../design/components/button/ExportIconButton';
import { SideDrawer } from '../../../design/components/drawer/SideDrawer';
import { FilterDrawerTrigger } from '../../common/FilterHelpers';
import { FilterSidebar } from '../../common/filter-sidebar/FilterSidebar';

export type TableBarSortOption<T = string> = {
    readonly direction: 'asc' | 'desc';
    readonly field: T;
    readonly label?: string;
};

type TableBarProps = {
    readonly filters: readonly FilterWithFilterStates[];
    readonly hasActiveFilters: boolean;
    readonly isExporting: boolean;
    readonly onExportButtonClick: MouseEventHandler<HTMLButtonElement>;
    readonly onSearchButtonClick: MouseEventHandler<HTMLButtonElement>;
    readonly resetFilterValues: () => void;
    readonly saveFilterValues: (updatedFilters: readonly FilterWithFilterStates[]) => void;
};

type TableBarWithSortProps<T extends string> = TableBarProps & {
    readonly hasActiveSortOption: boolean;
    readonly onSortOptionSelected: (sortOption: TableBarSortOption<T>) => void;
    readonly sortOptions: readonly TableBarSortOption<T>[];
};

const isWithSortOptions = <T extends string>(
    options: TableBarProps | TableBarWithSortProps<T>,
): options is TableBarWithSortProps<T> => 'onSortOptionSelected' in options && 'sortOptions' in options;

export const TableBar = <T extends string = string>(
    options: TableBarProps | TableBarWithSortProps<T>,
    // biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration
): ReactElement<any> => {
    const {
        filters,
        hasActiveFilters,
        isExporting,
        onExportButtonClick,
        onSearchButtonClick,
        resetFilterValues,
        saveFilterValues,
    } = options;
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const { translate } = useContext(ApplicationContext);

    const handleOpenSortMenu = (event: MouseEvent<HTMLButtonElement>): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseSortMenu = (): void => {
        setAnchorEl(null);
    };

    return (
        <Fragment>
            <IconButton aria-label="Search" color="secondary" onClick={onSearchButtonClick}>
                <SearchIcon />
            </IconButton>
            {/** biome-ignore lint/complexity/noUselessFragments: suppressed due to migration */}
            <Fragment>
                <FilterDrawerTrigger hasActiveFilters={hasActiveFilters} onClick={() => setIsFilterDrawerOpen(true)} />
                <SideDrawer
                    open={isFilterDrawerOpen}
                    title={translate('pages.job-detail.recommendations.drawer.headline')}
                    onClose={() => setIsFilterDrawerOpen(false)}
                >
                    <FilterSidebar
                        filters={filters}
                        onResetFilters={() => {
                            resetFilterValues();
                            setIsFilterDrawerOpen(false);
                        }}
                        onSubmitFilters={(updatedFilters) => {
                            saveFilterValues(updatedFilters);
                            setIsFilterDrawerOpen(false);
                        }}
                    />
                </SideDrawer>
            </Fragment>
            {isWithSortOptions<T>(options) && (
                <Fragment>
                    <IconButton
                        aria-label="Sort"
                        color={options.hasActiveSortOption ? 'primary' : 'secondary'}
                        onClick={handleOpenSortMenu}
                    >
                        <SwapVertical />
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            horizontal: 'right',
                            vertical: 'bottom',
                        }}
                        open={Boolean(anchorEl)}
                        transformOrigin={{
                            horizontal: 'right',
                            vertical: 'top',
                        }}
                        onClick={handleCloseSortMenu}
                        onClose={handleCloseSortMenu}
                    >
                        {options.sortOptions.map((sortOption) => (
                            <MenuItem
                                key={`sort-option-${sortOption.field}-${sortOption.direction}`}
                                aria-label={sortOption.label}
                                onClick={() => options.onSortOptionSelected(sortOption)}
                            >
                                <ListItemText>{sortOption.label}</ListItemText>
                            </MenuItem>
                        ))}
                    </Menu>
                </Fragment>
            )}
            <ExportIconButton
                aria-label="Export"
                disabled={isExporting}
                isPending={isExporting}
                tooltip={translate(isExporting ? 'common.exporting' : 'common.export')}
                onClick={onExportButtonClick}
            />
        </Fragment>
    );
};
